import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  OpenMatchModel,
  MatchesFilters,
  listOpenMatches,
  completeOpenMatch,
  cancelOpenMatch,
  deleteOpenMatch,
  updateOpenMatch,
} from '@/admin/api/matches';
import type { Pagination } from '@/admin/api/courts';

export interface AdminMatchesState {
  items: OpenMatchModel[];
  pagination: { page: number; pageSize: number; total?: number; pageCount?: number };
  filters: MatchesFilters;
  loading: boolean;
  error?: string;
}

const initialState: AdminMatchesState = {
  items: [],
  pagination: { page: 1, pageSize: 10, total: 0, pageCount: 1 },
  filters: { page: 1, pageSize: 10 },
  loading: false,
};

export const fetchMatches = createAsyncThunk<Pagination<OpenMatchModel>, void>(
  'adminMatches/fetchMatches',
  async (_arg, thunkApi) => {
    const state = thunkApi.getState() as any;
    const { filters } = state.adminMatches as AdminMatchesState;
    return await listOpenMatches(filters);
  }
);

export const completeMatchThunk = createAsyncThunk<void, { id: number }>(
  'adminMatches/complete',
  async ({ id }) => {
    await completeOpenMatch(id);
  }
);

export const cancelMatchThunk = createAsyncThunk<void, { id: number }>(
  'adminMatches/cancel',
  async ({ id }) => {
    await cancelOpenMatch(id);
  }
);

export const deleteMatchThunk = createAsyncThunk<void, { id: number }>(
  'adminMatches/delete',
  async ({ id }) => {
    await deleteOpenMatch(id);
  }
);

export const updateMatchThunk = createAsyncThunk<OpenMatchModel, { id: number; payload: Partial<OpenMatchModel> }>(
  'adminMatches/update',
  async ({ id, payload }) => {
    return await updateOpenMatch(id, payload);
  }
);

const adminMatchesSlice = createSlice({
  name: 'adminMatches',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<MatchesFilters>>) {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    setPage(state, action: PayloadAction<number>) {
      state.filters.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.filters.pageSize = action.payload;
      state.filters.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatches.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.pagination = {
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total,
          pageCount: action.payload.pageCount,
        };
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load matches';
      })
      .addCase(completeMatchThunk.fulfilled, (state, action) => {
        const id = (action.meta.arg as { id: number }).id;
        const item = state.items.find(i => i.id === id || i.matchId === id);
        if (item) item.status = 'ARCHIVED';
      })
      .addCase(cancelMatchThunk.fulfilled, (state, action) => {
        const id = (action.meta.arg as { id: number }).id;
        const item = state.items.find(i => i.id === id || i.matchId === id);
        if (item) item.status = 'CANCELED';
      })
      .addCase(deleteMatchThunk.fulfilled, (state, action) => {
        const id = (action.meta.arg as { id: number }).id;
        state.items = state.items.filter(i => (i.id ?? i.matchId) !== id);
      })
      .addCase(updateMatchThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.items.findIndex(i => i.id === updated.id || i.matchId === updated.id);
        if (idx >= 0) state.items[idx] = { ...state.items[idx], ...updated };
      });
  },
});

export const { setFilters, setPage, setPageSize } = adminMatchesSlice.actions;
export default adminMatchesSlice;
